export type BarcodeFormat = {
  pattern?: string
  placeholder?: string
  label?: string
  barcodeLength?: number
}
export type TicketTypeBarcodeFormats = {
  ticketType: string
  label: string
  formats: BarcodeFormat[]
}
export type TicketBarcodeFormats = TicketTypeBarcodeFormats[]

/** The barcode formats for a claim form game type, or an empty list when it has none. */
export function findTicketTypeFormats(
  formats: TicketBarcodeFormats,
  gameType: string,
): BarcodeFormat[] {
  return formats.find((entry) => entry.label === gameType)?.formats ?? []
}

export enum SectionState {
  LOCKED = 'locked',
  UNLOCKED = 'unlocked',
  EDITING = 'editing',
}

/** Two-column field pairs for the claimant section (read-only view) */
export const CLAIMANT_ROWS: [string, string | null][] = [
  ['firstName', 'middleInitial'],
  ['lastName', 'suffix'],
  ['socialSecurityNumber', 'dateOfBirth'],
  ['usCitizen', 'countryOfResidence'],
  ['originalClaimant', null],
]

/** Two-column field pairs for the claimant section (editable view, minus countryOfResidence handled separately) */
export const EDITABLE_CLAIMANT_ROWS: [string, string | null][] = [
  ['firstName', 'middleInitial'],
  ['lastName', 'suffix'],
  ['socialSecurityNumber', 'dateOfBirth'],
  ['usCitizen', 'originalClaimant'],
]

/** Where the contact section's fields live in the schema, for label and definition lookups. */
export const CONTACT_SECTION_PATH = ['contact']

/** Two-column field pairs for the contact section, in both the read-only and editable views */
export const CONTACT_ROWS: [string, string | null][] = [
  ['mailingAddress1', 'mailingAddress2'],
  ['city', 'region'],
  ['postalCode', null],
  ['mobilePhoneNumber', 'secondaryContactNumber'],
  ['emailAddress', null],
]

/**
 * Short display labels for the back-office, only for fields where the schema's
 * player-facing label is too long or verbose. All other fields use the schema label directly.
 */
export const BACK_OFFICE_LABEL_OVERRIDES: Record<string, string> = {
  usCitizen: 'US Citizen?',
  originalClaimant: 'Original Claimant?',
  socialSecurityNumber: 'SSN',
  emailAddress: 'Email',
}

/** Ticket item properties the drawer lays out explicitly; everything else renders generically. */
export const KNOWN_TICKET_FIELDS = new Set(['prizeAmount', 'gameType', 'barcodeNumber'])

/**
 * Schema meta flag marking a property an agent must fill in before a claim form can be received.
 * Must match the string PDS emits in the schema — see `x-required-before-received` there.
 */
export const REQUIRED_BEFORE_RECEIVED = 'x-required-before-received'

/**
 * Schema meta flag marking a property the claim form search matches against, so the search copy can
 * name it. Must match the string PDS emits in the schema — see `x-searchable` there.
 */
export const SEARCHABLE = 'x-searchable'

/**
 * Schema meta flag marking a value too sensitive for the plaintext search column, so PDS withholds it
 * even when it is also flagged searchable. Must match the string PDS emits in the schema.
 */
export const SENSITIVE = 'sensitive'

export type InputType = 'date' | 'datetime-local' | 'email' | 'number' | 'tel' | 'text'

export function formatBarcode(value: string, placeholder: string): string {
  const digits = value.replace(/\D/g, '')
  let result = ''
  let digitIndex = 0
  for (let i = 0; i < placeholder.length && digitIndex < digits.length; i++) {
    if (placeholder[i] === '-') {
      result += '-'
    } else {
      result += digits[digitIndex]
      digitIndex++
    }
  }
  return result
}
